import styled, { css } from 'styled-components'
import { SectionColorKey } from './AnalysisSidebar.types'

/**
 * Full-saturation icon colors matching theme.colors.tags.* backgrounds.
 * Used for icon strokes inside the pill badges.
 */
const sectionIconColors: Record<SectionColorKey, string> = {
  steel: '#64748B',
  bronze: '#92400E',
  forest: '#166534',
  deepBlue: '#1E3A8A',
  wine: '#9F1239',
}

export const SidebarWrapper = styled.div`
  position: relative;

  @media (max-width: 1024px) {
    position: fixed;
    top: calc(48px + 84px); /* header + property header height */
    left: 0;
    bottom: 0;
    z-index: 100;
  }
`

export const SidebarContainer = styled.nav<{ $isOpen: boolean }>`
  width: 180px;
  background: ${({ theme }) => theme.colors.background.primary};
  border-right: 1px solid ${({ theme }) => theme.colors.border.default};
  overflow-y: auto;
  overflow-x: hidden;
  flex-shrink: 0;
  scrollbar-width: none;
  -ms-overflow-style: none;
  height: 100%;
  transition: transform 0.3s ease;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 1024px) {
    position: fixed;
    top: var(--header-height, 48px);
    left: 0;
    height: calc(100vh - var(--header-height, 48px));
    height: calc(100dvh - var(--header-height, 48px));
    z-index: 100;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    transform: translateX(${(props) => (props.$isOpen ? '0' : '-100%')});
    box-shadow: ${({ theme, $isOpen }) =>
      $isOpen ? theme.shadows.md : 'none'};
  }
`

export const SidebarToggleButton = styled.button<{ $isOpen: boolean }>`
  display: none;

  @media (max-width: 1024px) {
    display: ${(props) => (props.$isOpen ? 'none' : 'flex')};
    position: fixed;
    left: 16px;
    bottom: 20px;
    width: 48px;
    height: 48px;
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1.5px solid ${({ theme }) => theme.colors.border.default};
    border-radius: ${({ theme }) => theme.borderRadius.circle};
    cursor: pointer;
    align-items: center;
    justify-content: center;
    z-index: 101;
    box-shadow: ${({ theme }) => theme.shadows.default};
    transition: all 0.2s ease;

    &:active {
      transform: scale(0.92);
      background: rgba(255, 255, 255, 0.95);
    }

    /* Disable hover effects on touch devices */
    @media (hover: none) {
      &:hover {
        background: rgba(255, 255, 255, 0.85);
      }

      &:active {
        transform: scale(0.92);
        background: rgba(255, 255, 255, 0.95);
      }
    }
  }
`

export const MobileOverlay = styled.div<{ $isOpen: boolean }>`
  display: none;

  @media (max-width: 1024px) {
    display: ${(props) => (props.$isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ theme }) => theme.colors.surface.overlay};
    z-index: 99;
  }
`

export const SidebarContent = styled.div`
  padding: 12px 0px 0px 0px;
`

export const NavSection = styled.div`
  margin-bottom: 26px;

  &:first-of-type {
    margin-top: 6px;
  }
`

export const NavTitle = styled.h3<{ $sectionColor?: SectionColorKey }>`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ $sectionColor }) =>
    $sectionColor ? sectionIconColors[$sectionColor] : 'inherit'};
  margin-bottom: 6px;
  padding: 0 20px;
`

export const NavItem = styled.a<{
  $active?: boolean
  $sectionColor?: SectionColorKey
}>`
  display: flex;
  align-items: center;
  padding: 6px 20px;
  text-decoration: none;
  font-size: 11px;
  transition: all 0.2s;
  position: relative;
  cursor: pointer;
  outline: none;
  box-shadow: none;

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }

  ${({ theme, $active, $sectionColor }) => {
    const activeColor = $sectionColor
      ? sectionIconColors[$sectionColor]
      : theme.colors.primary
    const activeBg = $sectionColor
      ? theme.colors.tags[$sectionColor]
      : theme.colors.primaryLight

    if ($active) {
      return css`
        color: ${activeColor};
        background: ${activeBg};

        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 3px;
          background: ${activeColor};
          border-radius: 0 2px 2px 0;
        }
      `
    }

    return css`
      color: ${theme.colors.text.secondary};
      background: transparent;
    `
  }}

  &:hover {
    ${({ theme, $active, $sectionColor }) => {
      const activeColor = $sectionColor
        ? sectionIconColors[$sectionColor]
        : theme.colors.primary
      const activeBg = $sectionColor
        ? theme.colors.tags[$sectionColor]
        : theme.colors.primaryLight

      if ($active) {
        return css`
          color: ${activeColor};
          background: ${activeBg};
        `
      }
      return css`
        color: ${theme.colors.text.primary};
        background: ${theme.colors.background.secondary};
      `
    }}
  }

  /* Mobile: Ensure 44px minimum touch target */
  @media (max-width: 1024px) {
    min-height: ${({ theme }) => theme.touchTargets.minimum};
    padding: 10px 20px;
  }

  /* Disable hover effects on touch devices */
  @media (hover: none) {
    &:hover {
      ${({ theme, $active, $sectionColor }) => {
        const activeColor = $sectionColor
          ? sectionIconColors[$sectionColor]
          : theme.colors.primary
        const activeBg = $sectionColor
          ? theme.colors.tags[$sectionColor]
          : theme.colors.primaryLight

        if ($active) {
          return css`
            color: ${activeColor};
            background: ${activeBg};
          `
        }
        return css`
          color: ${theme.colors.text.secondary};
          background: transparent;
        `
      }}
    }

    &:active {
      ${({ theme, $active, $sectionColor }) => {
        const activeBg = $sectionColor
          ? theme.colors.tags[$sectionColor]
          : theme.colors.primaryLight

        if ($active) {
          return css`
            background: ${activeBg};
          `
        }
        return css`
          background: ${theme.colors.background.secondary};
        `
      }}
    }
  }
`

export const NavFilterDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  margin-left: auto;
  flex-shrink: 0;
`

export const NavCount = styled.span`
  margin-left: auto;
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.tertiary};
  flex-shrink: 0;
`

export const NavIcon = styled.span<{
  $active?: boolean
  $sectionColor?: SectionColorKey
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 6px;

  ${({ theme, $sectionColor, $active }) => {
    const color = $sectionColor
      ? sectionIconColors[$sectionColor]
      : theme.colors.primary
    const bg = $sectionColor
      ? theme.colors.tags[$sectionColor]
      : theme.colors.primaryLight

    if ($active) {
      return css`
        background: ${bg};
        color: ${color};
      `
    }
    if ($sectionColor) {
      return css`
        background: ${bg};
        color: ${color};
      `
    }
    return css`
      background: ${theme.colors.tags.gray};
      color: ${theme.colors.text.tertiary};
    `
  }}
`
